/**
 * L1-L8 progress for one Business Discovery. INTERNAL only.
 *
 * Read straight from the discovery status the backend returns: a stage is
 * complete when it is in `completed_levels`, current when it is
 * `current_level`, and — once the conversation has closed — a current stage
 * that was never completed is where it ended. Nothing is inferred.
 */

import { DISCOVERY_LAYERS } from '../config';
import '../styles/DiscoveryStage.css';

const STATE_TEXT = {
  done: 'Complete',
  current: 'In progress',
  ended: 'Ended here',
  todo: 'Not reached',
};

export default function DiscoveryStage({ discovery }) {
  if (!discovery) return null;

  const done = new Set(discovery.completed_levels || []);
  const current = discovery.current_level;
  const cited = discovery.documents_cited || [];
  const completeCount = DISCOVERY_LAYERS.filter((layer) => done.has(layer.id)).length;
  const percent = Math.round((completeCount / DISCOVERY_LAYERS.length) * 100);

  return (
    <div className="nsc-progress">
      <div className="nsc-progress__summary">
        <span className="nsc-progress__count">
          {completeCount} of {DISCOVERY_LAYERS.length} stages complete
        </span>
        <span className="nsc-progress__percent">{percent}%</span>
      </div>
      <div
        className="nsc-progress__bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={DISCOVERY_LAYERS.length}
        aria-valuenow={completeCount}
        aria-label="Discovery stages complete"
      >
        <span style={{ width: `${percent}%` }} />
      </div>

      <ol className="nsc-steps">
        {DISCOVERY_LAYERS.map((layer) => {
          let state = 'todo';
          if (done.has(layer.id)) state = 'done';
          else if (layer.id === current) state = discovery.closed ? 'ended' : 'current';
          return (
            <li key={layer.id} className={`nsc-step nsc-step--${state}`}>
              <span className="nsc-step__marker" aria-hidden="true">
                {state === 'done' ? '✓' : ''}
              </span>
              <span className="nsc-step__code">{layer.id}</span>
              <span className="nsc-step__label">{layer.label}</span>
              <span className="nsc-step__state">{STATE_TEXT[state]}</span>
            </li>
          );
        })}
      </ol>

      {/* Which uploaded documents the conversation drew on. Never the
          text of them — the files themselves are in the Documents card. */}
      {cited.length > 0 && (
        <p className="nsc-progress__note">
          Drew on {cited.length === 1 ? 'document' : 'documents'}: {cited.join(', ')}
        </p>
      )}
    </div>
  );
}
